import { useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import type {
  PMSSchedulesCalendarProps,
  AppointmentData,
  OperatoryData,
} from 'app/components/scheduling/PMSSchedulesCalendar'
import { APPOINTMENT_TYPE_COLORS } from 'app/components/scheduling/PMSSchedulesCalendar'
import { logError } from 'lib/logging'

export type UsePmsSchedulesLocationReturn = Omit<PMSSchedulesCalendarProps, 'showPageHeader'>

const PMS_SCHEDULES_LOCATION_QUERY = graphql(`
  query PmsSchedulesLocation($locationId: ID!) {
    location(id: $locationId) {
      id
      name
      kollaConsumerId
    }
  }
`)

// Blockouts and schedules come from indexed models; rooms, booked appointments,
// providers, and availabilities come live from the PMS via the REST
// scheduling-data endpoint below.
const PMS_SCHEDULES_QUERY = graphql(`
  query PmsSchedules($schedulesSearch: String, $pagination: PaginationInput, $locationId: ID!) {
    resourceSchedules(search: $schedulesSearch, pagination: $pagination) {
      hits {
        id
        name
        date
        blocks {
          startTime
          endTime
        }
        notes {
          startTime
          endTime
          note
          additionalData {
            appointmentType
          }
        }
      }
      total
    }
    appointmentTypes(locationId: $locationId, pagination: { limit: 200 }) {
      hits {
        id
        vendorId
        displayName
        appointmentLength
        activeScheduler
      }
    }
  }
`)

type RestSchedulingData = {
  operatories: OperatoryData[]
  appointments: AppointmentData[]
  availabilities: { providerId: string; operatoryId: string }[]
  providers: { id: string; vendorId: string; displayName: string; resourceName: string }[]
  pmsStatus?: `ok` | `error`
  pmsError?: string
}

export function usePmsSchedulesLocation(): UsePmsSchedulesLocationReturn {
  const { locationId } = useParams()
  const [searchParams] = useSearchParams()
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const date = searchParams.get(`date`) || today

  const [locationResult] = useQuery({
    query: PMS_SCHEDULES_LOCATION_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })
  const location = locationResult.data?.location
  // ResourceSchedule records key off kollaConsumerId, falling back to the
  // location id (how the sync writes them).
  const consumerId = location?.kollaConsumerId || location?.id
  const schedulesSearch = `kollaConsumerId:${consumerId} AND date:${date}`

  const [schedulesResult] = useQuery({
    query: PMS_SCHEDULES_QUERY,
    variables: {
      locationId: locationId!,
      schedulesSearch,
      pagination: { limit: 200 },
    },
    pause: !locationId || !consumerId,
  })

  const schedules = useMemo(
    () =>
      (schedulesResult.data?.resourceSchedules?.hits ?? []).map((s) => ({
        name: s.name,
        blocks: s.blocks ?? [],
        notes: (s.notes ?? []).map((n) => ({
          note: n.note,
          startTime: n.startTime,
          endTime: n.endTime,
          additionalData: { appointmentType: n.additionalData?.appointmentType ?? `` },
        })),
      })),
    [schedulesResult.data?.resourceSchedules?.hits],
  )

  // Blockout notes reference appointment types by vendor id ("appointmenttypes/44"),
  // so key by vendorId and show the PMS display name.
  const appointmentTypes = useMemo(
    () =>
      (schedulesResult.data?.appointmentTypes?.hits ?? [])
        .filter((at) => at.vendorId && at.displayName)
        .sort((a, b) => (a.displayName || ``).localeCompare(b.displayName || ``))
        .map((at, i) => ({
          id: at.vendorId!,
          name: at.displayName!,
          duration: at.appointmentLength,
          enabled: at.activeScheduler ?? false,
          color: APPOINTMENT_TYPE_COLORS[i % APPOINTMENT_TYPE_COLORS.length],
        })),
    [schedulesResult.data?.appointmentTypes?.hits],
  )

  // Rooms, booked appointments, providers, and availabilities come from the PMS
  // through the REST scheduling-data endpoint. A pmsStatus of "error" means the
  // PMS did not answer — that must not look the same as an empty day.
  const [restData, setRestData] = useState<RestSchedulingData & { loaded: boolean }>({
    operatories: [],
    appointments: [],
    availabilities: [],
    providers: [],
    loaded: false,
  })

  useEffect(() => {
    if (!locationId) return
    let cancelled = false
    fetch(`/api/scheduling/locations/${locationId}/scheduling-data?day=${date}`, {
      credentials: `include`,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`scheduling-data request failed with ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        setRestData({
          operatories: data.operatories ?? [],
          appointments: data.appointments ?? [],
          availabilities: (data.availabilities ?? [])
            .filter((a: { providerId?: string; operatoryId?: string }) => a.providerId && a.operatoryId)
            .map((a: { providerId: string; operatoryId: string }) => ({
              providerId: a.providerId,
              operatoryId: a.operatoryId,
            })),
          providers: data.providers ?? [],
          pmsStatus: data.pmsStatus,
          pmsError: data.pmsError,
          loaded: true,
        })
      })
      .catch((err) => {
        logError(`Error fetching PMS data`, err)
        if (cancelled) return
        setRestData({
          operatories: [],
          appointments: [],
          availabilities: [],
          providers: [],
          pmsStatus: `error`,
          pmsError: err instanceof Error ? err.message : `PMS request failed`,
          loaded: true,
        })
      })
    return () => {
      cancelled = true
    }
  }, [locationId, date])

  const pmsError = restData.loaded && restData.pmsStatus === `error` ? restData.pmsError : undefined

  // Reject the deferred promises on a PMS failure so the calendar shows its
  // "PMS unreachable" banner instead of drawing an empty grid.
  const operatoriesPromise = useMemo(() => {
    if (!restData.loaded) return null
    if (pmsError) return Promise.reject(new Error(pmsError))
    return Promise.resolve(restData.operatories)
  }, [restData, pmsError])

  const appointmentsPromise = useMemo(() => {
    if (!restData.loaded) return null
    if (pmsError) return Promise.reject(new Error(pmsError))
    return Promise.resolve(restData.appointments)
  }, [restData, pmsError])

  // Providers straight from the PMS payload — already scoped to this location.
  const providers = useMemo(
    () =>
      restData.providers.map((p) => ({
        id: p.vendorId,
        name: p.displayName,
        resourceName: p.resourceName,
        providerId: p.id,
      })),
    [restData.providers],
  )

  return {
    location: location ?? { name: ``, id: locationId },
    date,
    schedules,
    providers,
    operatoriesPromise,
    appointmentTypes,
    availabilities: restData.availabilities,
    appointmentsPromise,
  }
}
